import { reatomAsync } from '@reatom/framework'
import i18next from 'i18next'
import { nanoid } from 'nanoid'

import { Result } from '@blade/std-lib'
import { CurrencyModel } from '@root/entities/currency/model'
import { fetchQuoteRequestAction } from '@root/entities/quote/atoms'
import { joinedInviteCodeAtom } from '@root/entities/referral/atoms'
import { Error, UnknownError } from '@root/shared'
import { showSnackbarAction } from '@root/shared/ui-kit'

import { SwapRequestSnackbar } from '../ui/desktop/swap-request-snackbar'

import { processSwapAction } from './reatom-swap'
import { activeSwapsAtom } from './reatom-swap-requests'

import type { ProcessSwapArgs } from './swap-requests'
import type { SwapRequest } from '../model/swap-request-model'
import type { TypeHelper } from '@blade/std-lib'
import type { Quote } from '@root/entities/quote'

export type BuySwapArgs = TypeHelper.OmitKey<SwapRequest, 'id' | 'type'> & {
  quote: Quote.Quote | undefined
}

export const processBuySwapAction = reatomAsync(
  async (ctx, request: BuySwapArgs) => {
    const {
      amount,
      pair,
      wallet,
      settings,
      quote: quote_,
      baseCurrencyOverride,
    } = request

    const currencyIn = baseCurrencyOverride ?? pair.base
    const currencyOut = pair.quote

    const baseAssetBalance = ctx.get(
      wallet.assetBalance(CurrencyModel.toNativeIfWrapped(currencyIn).address),
    )
    if (amount.gt(baseAssetBalance)) {
      showSnackbarAction(ctx, {
        intent: 'error',
        text: i18next.t('swap.error'),
        description: i18next.t('trading.error.insufficient-funds', {
          ticker: currencyIn.ticker,
        }),
      })
      return Result.fail(UnknownError.fromUnknown('Insufficient funds'))
    }

    const model = activeSwapsAtom.create(ctx, {
      ...request,
      type: 'buy',
      id: nanoid(),
    })
    showSnackbarAction(ctx, {
      delay: 'permanent',
      onClose: () => {
        activeSwapsAtom.delete(ctx, model.request.id)
      },
      element: <SwapRequestSnackbar model={model} />,
    })

    const quoteResult = await ctx.schedule(() => {
      if (quote_ && quote_.inputAmount.eq(amount)) {
        return Result.succeed(quote_)
      }

      return fetchQuoteRequestAction(ctx, {
        amount: amount,
        currencyIn,
        currencyOut,
        network: pair.network,
        poolAddress: pair.address,
        providerId: pair.provider.id,
        type: 'EXACT_INPUT',
        wallet: wallet,
        inviteCode: ctx.get(joinedInviteCodeAtom),
        settings,
      })
    })

    if (Result.isFailure(quoteResult)) {
      const error = Error.UnknownError.fromUnknown('Quote error')
      model.handleError(ctx, error)
      return Result.fail(error)
    }

    const quote = quoteResult.success
    model.handleQuote(ctx, quote)
    const accounts = ctx.get(pair.accounts)
    const pairQuoteTokenProgram = ctx.get(pair.quoteTokenProgram)!

    const args: ProcessSwapArgs = {
      id: nanoid(),
      amount: amount,
      inAddress: currencyIn.address,
      outAddress: currencyOut.address,
      network: pair.network,
      pairAddress: pair.address,
      provider: pair.provider,
      pairQuoteCurrency: currencyOut.address,
      swapSettings: settings,
      walletId: wallet.id,
      walletAddress: wallet.address,
      pairQuoteTokenProgram,
      type: 'buy',
      quote,
      accounts,
    }

    const result = await ctx.schedule(() => processSwapAction(ctx, args))

    if (Result.isFailure(result)) {
      model.handleError(ctx, result.failure)
      return result
    }
    model.handleSendSwapData(ctx, result.success)

    return result
  },
  'processBuySwapAction',
)
